import type { ISite } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { COMPETITORS_AFFECT_BENCHMARK } from "app/pages/activation/modules/Settings/text";
import { AssetKey } from "app/pages/activation/modules/Settings/types";
import { activationTracking } from "app/pages/activation/services/tracking";

import { generateAutomationName } from "../../../helpers/generateAutomationName";

import { DomainListAutocomplete } from "./components/DomainListAutocomplete";
import { DomainListSectionHeader } from "./components/DomainListSectionHeader";
import {
    FULL_AUTOCOMPLETE_HEIGHT,
    MAX_ALLOWED_MAIN_COMPETITORS,
    MIN_AUTOCOMPLETE_HEIGHT,
    MIN_COMPETITORS_LENGTH,
} from "./constants";
import { SettingsMainCompetitor } from "./SettingsMainCompetitor/SettingsMainCompetitor";
import { COMPETITORS_SUB_TITLE, COMPETITORS_TITLE } from "./texts";
import type { CompetitorsSchema, IProjectDomainAsset } from "./types";

const COMPETITORS_DATA_AUTOMATION_PREFIX = ["edit", "competitors"];

export const Competitors = ({
    isLoading,
    selectedMainDomain,
    allCompetitors,
    addCompetitor,
    removeAllCompetitors,
    removeCompetitor,
    isViewerMode,
}: CompetitorsSchema) => {
    const maxCompetitors = ActivationComponentAssets.competitorsLimit;
    const currentCompetitorsNumber = allCompetitors.competitors.length;

    const isMaxCompetitorsSelected = currentCompetitorsNumber >= maxCompetitors;

    const isMaxMainCompetitorsSelected = allCompetitors.mainCompetitors.length >= MAX_ALLOWED_MAIN_COMPETITORS;

    const isMainCompetitorsActive = currentCompetitorsNumber > MAX_ALLOWED_MAIN_COMPETITORS;

    const selectedMainCompetitorsNames = allCompetitors.mainCompetitors.map(({ name }) => name);

    const autocompleteHeight =
        allCompetitors.competitors.length >= MIN_COMPETITORS_LENGTH
            ? FULL_AUTOCOMPLETE_HEIGHT
            : MIN_AUTOCOMPLETE_HEIGHT;

    const removeItemFromSelected = (selectedItem: ISite, assetKey?: AssetKey, isRemoveAll = false) => {
        if (isRemoveAll) {
            const isActive = allCompetitors.competitors.length - 1 > MAX_ALLOWED_MAIN_COMPETITORS;

            removeAllCompetitors(selectedItem, assetKey, isActive);
        } else {
            removeCompetitor(selectedItem, assetKey);
        }
    };

    const onClick = (domain: IProjectDomainAsset, clearTextField: () => void) => {
        const hasDomain = allCompetitors.competitors?.some(({ name }) => name === domain?.name);

        if (isMaxCompetitorsSelected || hasDomain) {
            return;
        }

        addCompetitor(domain, AssetKey.competitors);
        clearTextField();
    };

    const onSelectItemClick = (competitor: IProjectDomainAsset) => () => {
        if (!isMainCompetitorsActive) {
            return;
        }
        const { name } = competitor;

        if (selectedMainCompetitorsNames?.includes(name)) {
            removeItemFromSelected(competitor, AssetKey.mainCompetitors);
        } else {
            if (isMaxMainCompetitorsSelected) {
                return;
            }

            addCompetitor(competitor, AssetKey.mainCompetitors);
        }
    };

    const onDelete = (competitor: IProjectDomainAsset) => () => {
        removeItemFromSelected(competitor, undefined, true);
        activationTracking.settings.trackDeleteCompetitor();
    };

    const getListItems = () => {
        return allCompetitors.competitors.map((competitor, index) => {
            const { name, image } = competitor;
            const isMainCompetitor = selectedMainCompetitorsNames.includes(name);
            const isDisabled = (isMaxMainCompetitorsSelected && !isMainCompetitor) || isViewerMode;

            return (
                <SettingsMainCompetitor
                    key={index + name}
                    isActive={isMainCompetitorsActive}
                    name={name}
                    image={image}
                    onSelect={onSelectItemClick(competitor)}
                    onDelete={onDelete(competitor)}
                    isDisabled={isDisabled}
                />
            );
        });
    };

    const autocompleteItemsExcludeList = [
        selectedMainDomain?.name,
        ...allCompetitors.competitors.map((suggested) => suggested.name),
    ];

    return (
        <Stack
            gap="24px"
            width="500px"
            data-automation={generateAutomationName("settings", [...COMPETITORS_DATA_AUTOMATION_PREFIX, "wrapper"])}
        >
            <DomainListSectionHeader
                currentSize={currentCompetitorsNumber}
                dataAutomationNames={COMPETITORS_DATA_AUTOMATION_PREFIX}
                limit={maxCompetitors}
                subtitle={translate(COMPETITORS_SUB_TITLE)}
                title={translate(COMPETITORS_TITLE)}
                tooltipText={translate(COMPETITORS_AFFECT_BENCHMARK)}
            />

            <Stack gap="12px">
                <DomainListAutocomplete
                    currentSize={currentCompetitorsNumber}
                    dataAutomationNames={COMPETITORS_DATA_AUTOMATION_PREFIX}
                    disabled={isViewerMode}
                    exclude={autocompleteItemsExcludeList}
                    height={autocompleteHeight}
                    isLoading={isLoading}
                    limit={maxCompetitors}
                    onItemClick={onClick}
                    primaryDomain={selectedMainDomain?.name}
                />
                <Stack>{getListItems()}</Stack>
            </Stack>
        </Stack>
    );
};
